export interface VehicleType {
  id: string;
  label: string;
  example: string;
  icon: string;
  multiplier: number;
}

export const VEHICLE_TYPES: VehicleType[] = [
  {
    id: 'moto',
    label: 'Moto / Ciclomotor',
    example: 'Honda CB, Yamaha, Vespa…',
    icon: 'ri-riding-line',
    multiplier: 0.7,
  },
  {
    id: 'utilitario',
    label: 'Utilitario pequeño',
    example: 'Seat Ibiza, VW Polo, Clio…',
    icon: 'ri-car-line',
    multiplier: 1.0,
  },
  {
    id: 'compacto',
    label: 'Compacto / Familiar',
    example: 'Golf, Focus, Astra, León…',
    icon: 'ri-car-fill',
    multiplier: 1.15,
  },
  {
    id: 'suv',
    label: 'SUV / 4x4',
    example: 'Tucson, Tiguan, Qashqai…',
    icon: 'ri-truck-line',
    multiplier: 1.3,
  },
  {
    id: 'furgoneta',
    label: 'Furgoneta / Monovolumen',
    example: 'Transit, Sprinter, Berlingo…',
    icon: 'ri-bus-line',
    multiplier: 1.45,
  },
];

export const AGE_OPTIONS = [
  { id: 'new', label: 'Menos de 5 años', multiplier: 1.0 },
  { id: 'mid', label: '5 – 10 años', multiplier: 1.05 },
  { id: 'older', label: '10 – 15 años', multiplier: 1.12 },
  { id: 'classic', label: 'Más de 15 años', multiplier: 1.2 },
];

interface VehicleStepProps {
  selectedType: VehicleType | null;
  selectedAge: typeof AGE_OPTIONS[number] | null;
  onSelectType: (v: VehicleType) => void;
  onSelectAge: (a: typeof AGE_OPTIONS[number]) => void;
}

const VehicleStep = ({ selectedType, selectedAge, onSelectType, onSelectAge }: VehicleStepProps) => (
  <div className="space-y-10">
    <div>
      <p className="text-zinc-400 text-sm text-center mb-6">
        ¿Qué tipo de vehículo tienes?{' '}
        <span className="text-yellow-400">Afecta al coste de las piezas y la mano de obra.</span>
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {VEHICLE_TYPES.map((v) => {
          const isSelected = selectedType?.id === v.id;
          return (
            <button
              key={v.id}
              onClick={() => onSelectType(v)}
              className={`cursor-pointer p-4 border transition-all duration-300 text-center group ${
                isSelected
                  ? 'border-yellow-400 bg-yellow-400/8'
                  : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-600'
              }`}
            >
              <div
                className={`w-10 h-10 flex items-center justify-center mx-auto mb-2.5 transition-colors ${
                  isSelected ? 'bg-yellow-400 text-black' : 'bg-zinc-800 text-zinc-400 group-hover:bg-zinc-700'
                }`}
              >
                <i className={`${v.icon} text-lg`} />
              </div>
              <p className={`text-xs font-bold uppercase tracking-wide leading-tight mb-1 ${isSelected ? 'text-yellow-400' : 'text-white'}`}>
                {v.label}
              </p>
              <p className="text-zinc-600 text-[10px] leading-snug">{v.example}</p>
              {isSelected && (
                <div className="mt-2 flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-yellow-400 text-sm" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>

    <div>
      <p className="text-zinc-400 text-sm text-center mb-6">
        ¿Cuántos años tiene el vehículo?{' '}
        <span className="text-yellow-400">Los vehículos más antiguos pueden requerir más trabajo.</span>
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {AGE_OPTIONS.map((a) => {
          const isSelected = selectedAge?.id === a.id;
          return (
            <button
              key={a.id}
              onClick={() => onSelectAge(a)}
              className={`cursor-pointer p-3.5 border transition-all duration-300 text-center ${
                isSelected
                  ? 'border-yellow-400 bg-yellow-400/8'
                  : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-600'
              }`}
            >
              <p className={`text-xs font-bold uppercase tracking-wide ${isSelected ? 'text-yellow-400' : 'text-white'}`}>
                {a.label}
              </p>
              {isSelected && (
                <i className="ri-checkbox-circle-fill text-yellow-400 text-sm mt-1.5 inline-block" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  </div>
);

export default VehicleStep;
